/**
 * useBinding.ts — POST /api/broker/v1/bindings hook
 *
 * On trigger: obtains a FRESH token via getToken() (never a possibly-stale
 * context token — fix 2026-08-02), sends POST with Bearer JWT, gets
 * redirect_uri, does window.location.href.
 * On error: transitions to 'error' state; TileCTA renders it inline
 * (tile.cta.launchError) — the failure is never silent.
 *
 * Design-contract §11: Binding Lifecycle
 * Ref: cycle 5 brief §3.3
 */

import { useState, useCallback } from 'react';

export type BindingState = 'idle' | 'submitting' | 'error';

export interface UseBindingResult {
  state: BindingState;
  trigger: () => Promise<void>;
}

export function useBinding(
  serviceId: string,
  getToken: () => Promise<string | null>,
): UseBindingResult {
  const [state, setState] = useState<BindingState>('idle');

  const trigger = useCallback(async () => {
    setState('submitting');
    const token = await getToken();
    if (!token) {
      setState('error');
      return;
    }
    try {
      const res = await fetch('/api/broker/v1/bindings', {
        cache: 'no-store',
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ service_id: serviceId }),
      });
      if (!res.ok) {
        setState('error');
        return;
      }
      const { redirect_uri } = await res.json() as { redirect_uri: string };
      // Navigate — state update after this is irrelevant
      window.location.href = redirect_uri;
    } catch {
      setState('error');
    }
  }, [serviceId, getToken]);

  return { state, trigger };
}
